// src/helpers/pdfRemitos.js
import { jsPDF } from "jspdf";
import { getRemitoById } from "./rutaRemitos";
import {
  fmtNum,
  S,
  loadImageAsDataURL,
  drawHeader,
} from "./pdfCommonSanPancracio";

const IVA_105 = 0.105;

/* ------------------------------ helpers layout ------------------------------ */

function ellipsize(doc, text, maxW) {
  let t = String(text ?? "");
  if (!t) return t;

  while (t.length > 0 && doc.getTextWidth(t + "…") > maxW) {
    t = t.slice(0, -1);
  }
  return t.length ? t + "…" : "…";
}

function drawPair(doc, x, y, label, value, w = 65, opts = {}) {
  const {
    wrap = false,
    maxLines = 2,
    boxH = wrap ? 16 : 12,
    valueFontSize = wrap ? 9 : 10,
    padding = 2,
    lineH = 4, // mm aprox para 9pt
  } = opts;

  // Label
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(S(label), x, y);

  // Value
  doc.setFont("helvetica", "normal");
  doc.setFontSize(valueFontSize);

  const text = S(value ?? "-");
  const innerW = w - padding * 2;

  let lines = wrap ? doc.splitTextToSize(text, innerW) : [text];

  if (wrap && lines.length > maxLines) {
    lines = lines.slice(0, maxLines);
    lines[maxLines - 1] = ellipsize(doc, lines[maxLines - 1], innerW);
  }

  const valueY = y + 6;
  lines.forEach((ln, i) => {
    doc.text(S(ln), x, valueY + i * lineH);
  });

  // Box
  doc.setDrawColor(200);
  doc.rect(x - 2, y - 4, w, boxH);
}

function drawSectionTitle(doc, y, text) {
  const pageW = doc.internal.pageSize.getWidth();
  doc.setDrawColor(120);
  doc.setFillColor(235, 235, 235);
  doc.rect(12, y - 6, pageW - 24, 10, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(S(text), 16, y + 1);
}

// Formatea fechas YYYY-MM-DD / ISO **sin** aplicar huso horario
function fmtDateRemito(fecha) {
  if (!fecha) return "-";

  if (fecha instanceof Date) {
    const y = fecha.getFullYear();
    const m = String(fecha.getMonth() + 1).padStart(2, "0");
    const d = String(fecha.getDate()).padStart(2, "0");
    return `${d}/${m}/${y}`;
  }

  const s = String(fecha);
  const iso = s.split("T")[0];
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return s;

  return `${d}/${m}/${y}`;
}

/* --------------------------------- main ---------------------------------- */

export async function generarPDFRemito(remitoId, opts = {}) {
  const resp = await getRemitoById(remitoId);
  if (!resp?.ok) {
    alert(resp?.error || "No se pudo obtener el remito.");
    return;
  }
  const r = resp.remito || {};

  // === Cálculo Compra A/B y Venta A/B (igual que en el formulario) ===
  const kgRecibidoNum = Number(r.kgRecibido || 0);
  const precioCompraNum = Number(r.precioCompra || 0);
  const precioVentaNum = Number(r.precioVenta || 0);
  const pctCompra = Number(r.porcentajeCompra || 0);
  const pctVenta = Number(r.porcentajeVenta || 0);

  const fracCompraNegro = Math.min(Math.max(pctCompra / 100, 0), 1);
  const fracVentaNegro = Math.min(Math.max(pctVenta / 100, 0), 1);
  const fracCompraBlanco = 1 - fracCompraNegro;
  const fracVentaBlanco = 1 - fracVentaNegro;

  const compraB = kgRecibidoNum * precioCompraNum * fracCompraNegro;
  const compraA =
    kgRecibidoNum * precioCompraNum * (1 + IVA_105) * fracCompraBlanco;

  const ventaB = kgRecibidoNum * precioVentaNum * fracVentaNegro;
  const ventaA =
    kgRecibidoNum * precioVentaNum * (1 + IVA_105) * fracVentaBlanco;

  const doc = new jsPDF({ unit: "mm", format: "a4" });

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 12;
  const contentW = pageW - margin * 2; // 186 en A4
  const gap = 2;

  let y = 12;

  // Logo + datos empresa
  const logoUrl = opts.logoUrl || "/img/logo-sanpancracio.png";
  const empresa = opts.empresa || {
    nombre: "SAN PANCRACIO SRL",
    linea1: "Avda. Peron 1600 - Oficina 29 Piso 1 - Yerba Buena - Tucumán",
    linea2: "Tel: 0381-3008829 • contacto@sanpancracio.com",
  };
  const logoDataURL = await loadImageAsDataURL(logoUrl);

  // Encabezado común
  drawHeader(doc, y, logoDataURL, empresa);
  y += 34;

  // Título Remito y nro/fecha
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(`REMITO N° ${S(r.codrem) || "-"}`, 12, y);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text(`Fecha: ${fmtDateRemito(r.fecha)}`, 140, y);
  y += 7;

  // ===================== DATOS PRINCIPALES =====================
  drawSectionTitle(doc, y, "Datos");
  y += 11;

  // 2 columnas
  const w2col = (contentW - gap) / 2; // ~92
  const xL = margin + 2;
  const xR = margin + w2col + gap + 2;

  const proveedorStr = r?.proveedor?.razonsocial ?? r?.proveedor ?? "-";
  const clienteStr = r?.cliente?.razonsocial ?? r?.cliente ?? "-";
  const transporteStr = r?.transporte?.transporte ?? r?.transporte ?? "-";
  const choferStr = r?.chofer?.nombre ?? r?.chofer ?? "-";
  const loteStr = r?.lote?.lote ? `${r.lote.lote}` : r?.lote ?? "-";
  const variedadStr = r.variedad || "-";

  // Fila 1
  drawPair(doc, xL, y, "Proveedor", proveedorStr, w2col, {
    wrap: true,
    maxLines: 2,
  });
  drawPair(doc, xR, y, "Cliente", clienteStr, w2col, {
    wrap: true,
    maxLines: 2,
  });
  y += 18;

  // Fila 2
  drawPair(doc, xL, y, "Chofer", choferStr, w2col, {
    wrap: true,
    maxLines: 2,
  });
  drawPair(doc, xR, y, "Lote", loteStr, w2col, {
    wrap: true,
    maxLines: 2,
  });
  y += 18;

  // Fila 3
  drawPair(doc, xL, y, "Transporte", transporteStr, w2col, {
    wrap: true,
    maxLines: 2,
  });
  drawPair(doc, xR, y, "Variedad", variedadStr, w2col, {
    wrap: true,
    maxLines: 2,
  });
  y += 18;

  // ===================== DETALLE DE CARGAS =====================
  drawSectionTitle(doc, y, "Detalle de Cargas");
  y += 11;

  const startX = 14;
  const rowH = 8; // ↓ antes 9
  const cols = 5;
  const totalW = contentW - 2;
  const colW = totalW / cols;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.5);

  const headers = [
    "KG Campo",
    "KG Recibidos",
    "Diferencia",
    "Precio Compra",
    "Precio Venta",
  ];
  headers.forEach((h, i) => {
    const x = startX + i * colW;
    doc.rect(x, y, colW, rowH);
    doc.text(S(h), x + 1.5, y + 5.3);
  });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);

  const vals = [
    fmtNum(r.kgCampo, 0),
    fmtNum(r.kgRecibido, 0),
    fmtNum(r.diferencia, 0),
    `$ ${fmtNum(r.precioCompra)}`,
    `$ ${fmtNum(r.precioVenta)}`,
  ];

  const y2 = y + rowH;
  vals.forEach((v, i) => {
    const x = startX + i * colW;
    doc.rect(x, y2, colW, rowH);
    doc.text(S(v), x + 1.5, y2 + 5.3);
  });

  y += rowH * 2 + 7; // ↓ antes +10

  // ===================== TOTALES Y REFERENCIAS =====================
  drawSectionTitle(doc, y, "Totales y Referencias");
  y += 11;

  const w1 = 70;
  const w2 = 70;
  const w3 = contentW - w1 - w2 - gap * 2;

  const x1 = margin + 2;
  const x2 = margin + w1 + gap + 2;
  const x3 = margin + w1 + gap + w2 + gap + 2;

  const step = 14; // ↓ antes 16

  drawPair(doc, x1, y, "Total Compra", `$ ${fmtNum(r.totalCompra)}`, w1);
  drawPair(doc, x2, y, "Total Venta", `$ ${fmtNum(r.totalVenta)}`, w2);
  drawPair(doc, x3, y, "Comisión", `$ ${fmtNum(r.comision)}`, w3);
  y += step;

  drawPair(
    doc,
    x1,
    y,
    "Compra A (blanco + IVA)",
    `$ ${fmtNum(compraA)}`,
    w1,
    { wrap: true, maxLines: 2 }
  );
  drawPair(doc, x2, y, "Compra B (negro)", `$ ${fmtNum(compraB)}`, w2);
  y += step;

  drawPair(
    doc,
    x1,
    y,
    "Venta A (blanco + IVA)",
    `$ ${fmtNum(ventaA)}`,
    w1,
    { wrap: true, maxLines: 2 }
  );
  drawPair(doc, x2, y, "Venta B (negro)", `$ ${fmtNum(ventaB)}`, w2);
  y += step;

  const facturaStr = r.factura ? "SI" : "NO";
  drawPair(doc, x1, y, "Nro DTV Compra", r.nroDTVCompra || "-", w1);
  drawPair(doc, x2, y, "Nro DTV Venta", r.nroDTVVenta || "-", w2);
  drawPair(doc, x3, y, "Factura", facturaStr, w3);
  y += step;

  drawPair(
    doc,
    x1,
    y,
    "% Compra B",
    r.porcentajeCompra != null ? `${fmtNum(r.porcentajeCompra, 2)} %` : "-",
    w1
  );
  drawPair(
    doc,
    x2,
    y,
    "% Venta B",
    r.porcentajeVenta != null ? `${fmtNum(r.porcentajeVenta, 2)} %` : "-",
    w2
  );
  drawPair(doc, x3, y, "Flete", `$ ${fmtNum(r.flete)}`, w3);
  y += step;

  // ===================== NOTAS (AUTO-FIT EN 1 HOJA) =====================
  drawSectionTitle(doc, y, "Notas");
  y += 11;

  const nota = S(r.nota || "").trim();

  // ancho útil para texto
  const maxWNotas = contentW - 4;

  // Altura disponible real hasta el pie
  const footerSafe = 16;
  const boxTop = y - 6;
  const maxHDisponible = pageH - footerSafe - boxTop;

  // line height aproximado en mm (fontSize está en pt)
  const lineHpt = (fontSize) => fontSize * 0.3528 * 1.15;

  doc.setFont("helvetica", "normal");

  if (!nota) {
    doc.setDrawColor(200);
    doc.rect(margin, y - 6, contentW, 12);
  } else {
    let size = 10;
    const minSize = 7;

    let lines = [];
    while (size >= minSize) {
      doc.setFontSize(size);
      lines = doc.splitTextToSize(nota, maxWNotas) || [];
      const h = 6 + lines.length * lineHpt(size);
      if (h <= maxHDisponible) break;
      size -= 0.5;
    }

    doc.setFontSize(Math.max(size, minSize));

    // si aun así no entra, recortamos líneas y ellipsis
    const lh = lineHpt(doc.getFontSize());
    const maxLines = Math.max(1, Math.floor((maxHDisponible - 6) / lh));

    if (lines.length > maxLines) {
      lines = lines.slice(0, maxLines);
      lines[maxLines - 1] = ellipsize(doc, lines[maxLines - 1], maxWNotas);
    }

    const boxH = Math.min(maxHDisponible, 6 + lines.length * lh);

    doc.setDrawColor(200);
    doc.rect(margin, y - 6, contentW, boxH);
    doc.text(lines, margin + 2, y);
  }

  // ===================== PIE =====================
  doc.setFont("helvetica", "italic");
  doc.setFontSize(9);
  doc.text(
    "Documento electrónico generado por el sistema San Pancracio",
    margin,
    pageH - 8
  );

  const filename = `Remito_${S(r.codrem) || remitoId}.pdf`;
  doc.save(filename);
}
